import moment from 'moment';
import React from 'react';
import { match } from 'ts-pattern';

import { Buttons } from '@/components';
import useCountdown from '@/hooks/useCountdown';
import { formatCurrency, formatDateCountdown } from '@/utils';

import { LOCK_OPTIONS, NetworkKey } from '../constants';
import { useLockContext } from '../LockContext';
import { LOCK_COLUMNS_ID } from './constants';
import type { LockedDataTable } from './helpers';
import useClaim from './useClaimForBsc';
import useClaimWithOraiNetwork from './useClaimForOrai';
import useWithdraw from './useWithdrawForBsc';
import useWithdrawWithOraiNetwork from './useWithdrawForOrai';

const LockTokenCell: React.FC<{
  data: LockedDataTable;
  columnKey: React.Key;
  handleWhenClaimed: (nonce: number) => void;
  handleWhenWithdraw: (nonce: number) => void;
}> = ({ data, columnKey, handleWhenClaimed, handleWhenWithdraw }) => {
  const { days, hours, minutes, seconds, isFinish } = useCountdown(data.unlock_date);
  const dateFormated = formatDateCountdown(0, hours, minutes, seconds);
  const { network } = useLockContext();
  const { handleClaim, loading } = useClaim();
  const { handleWithdraw, loading: loadingWithdraw } = useWithdraw();
  const { loading: loadingClaimOrai, handleClaim: handleClaimWithOraiNetwork } =
    useClaimWithOraiNetwork(handleWhenClaimed, Number(data.id));
  const { loading: loadingWithdrawOrai, handleWithdraw: handleWithdrawWithOraiNetwork } =
    useWithdrawWithOraiNetwork(handleWhenWithdraw, Number(data.id));
  const cellValue = data[columnKey as keyof LockedDataTable];
  const isCanClaim = Number(data.airi_reward) > 0;

  const renderCell = () => {
    return match(columnKey)
      .with(LOCK_COLUMNS_ID.EarnedReward, () => (
        <div className="flex justify-around sm:gap-6">
          <div className="text-center">
            <p className="font-semibold">{formatCurrency(Number(data.earned_credit), 3)}</p>
            <span className="text-[12px]">Credits</span>
          </div>
        </div>
      ))
      .with(LOCK_COLUMNS_ID.AvailableReward, () => (
        <div className="text-center">
          <p className="font-semibold text-green-500">
            {formatCurrency(Number(data.airi_reward), 6)}
          </p>
          <span className="text-[12px] text-green-500">AIRI</span>
        </div>
      ))
      .with(LOCK_COLUMNS_ID.Locked, () => (
        <span className="whitespace-nowrap">{formatCurrency(Number(data.locked), 3)} AIRI</span>
      ))
      .with(LOCK_COLUMNS_ID.Time, () =>
        days > 0 ? (
          <span className="whitespace-nowrap">{moment(data.unlock_date).format('ll')}</span>
        ) : (
          <div className="flex items-center gap-[3px]">
            <span>{isFinish ? '00' : dateFormated.hours}</span>
            <span>:</span>
            <span>{isFinish ? '00' : dateFormated.minutes}</span>
            <span>:</span>
            <span>{isFinish ? '00' : dateFormated.seconds}</span>
          </div>
        ),
      )
      .with(LOCK_COLUMNS_ID.Type, () => (
        <span className="whitespace-nowrap">
          {LOCK_OPTIONS.find((lock) => lock.value === data.type)?.label}
        </span>
      ))
      .with(LOCK_COLUMNS_ID.Action, () => (
        <div className="flex items-center justify-center gap-4">
          {!data.status && (
            <>
              <Buttons
                onClick={() => {
                  if (!isCanClaim) return;
                  network === NetworkKey.Bsc
                    ? handleClaim(Number(data.id), handleWhenClaimed)
                    : handleClaimWithOraiNetwork({
                        nonce: Number(data.id),
                        fees: 0,
                        gas: 400000,
                      });
                }}
                isLoading={loading || loadingClaimOrai}
                typeBtn={isCanClaim ? 'secondary' : 'disabled'}
                disabled={!isCanClaim}
                className="h-[34px]"
              >
                <span className="text-[13px] sm:text-[12px]">Claim</span>
              </Buttons>
              <Buttons
                isLoading={loadingWithdraw || loadingWithdrawOrai}
                typeBtn="secondary"
                className="h-[34px]"
                onClick={() => {
                  network === NetworkKey.Bsc
                    ? handleWithdraw(Number(data.id), handleWhenWithdraw)
                    : handleWithdrawWithOraiNetwork({
                        nonce: Number(data.id),
                        fees: 0,
                        gas: 400000,
                      });
                }}
              >
                <span className="text-[13px] sm:text-[12px]">Withdraw</span>
              </Buttons>
            </>
          )}
        </div>
      ))
      .otherwise(() => <span>{cellValue}</span>);
  };

  return renderCell();
};

export default LockTokenCell;
